import isEqual from 'lodash/isEqual';
import { useRoute, useRouter } from 'vue-router';
import type { UnwrapRef } from 'vue';
import { base64ToObject, objectToBase64 } from '@/libs/base64';
import { validateSearchOptions } from '.';
import {
  type DatasetRequest,
  type OffsetPaginationRequest,
  type FilterExpression,
  type GroupingFilterExpression,
  isGroupingFilterExpression,
} from './types';

export type SearchQueryParams = Omit<DatasetRequest, 'filter'> & {
  // We use the more specific GroupingFilterExpression so simplify the types on consumption
  filter?: GroupingFilterExpression;
  page?: OffsetPaginationRequest;
};

export function encodeSearchQueryParams(params: SearchQueryParams) {
  return { q: objectToBase64(params) };
}

export default function useSearchQueryParams<RequestExtras = void>(
  defaultVal?: MaybeRefOrGetter<DatasetRequest & RequestExtras>,
) {
  const router = useRouter();
  const route = useRoute();

  function getValuesFromQueryParam() {
    const q = route.query.q;
    if (!q || typeof q !== 'string') return;
    const obj = base64ToObject(q);
    if (!validateSearchOptions(obj)) {
      // invalid object so don't set and remove from query param
      router.replace({ query: { q: undefined } });
      return;
    }
    return obj;
  }

  const mergedDefaultVal = computed<SearchQueryParams & UnwrapRef<RequestExtras>>(() => ({
    filter: undefined,
    sort: undefined,
    page: { limit: 50, offset: 0 },
    ...toValue(defaultVal),
    ...getValuesFromQueryParam(),
  }));

  // eslint-disable-next-line vue/no-ref-object-reactivity-loss
  const params = reactive(mergedDefaultVal.value);

  const filterWithoutOperandIDs = (filter: FilterExpression | undefined) =>
    filter && {
      ...filter,
      operands: isGroupingFilterExpression(filter)
        ? filter.operands.map((op) => ({ ...op, id: undefined }))
        : undefined,
    };

  // Reset offset when filters change
  watch(
    () => params.filter,
    (newFilters, oldFilters) => {
      const filtersChanged = !isEqual(
        filterWithoutOperandIDs(newFilters),
        filterWithoutOperandIDs(oldFilters),
      );

      if (filtersChanged && params.page) {
        params.page.offset = 0;
      }
    },
  );

  const reset = async () => {
    await router.replace({ query: { q: undefined } });
    Object.assign(params, mergedDefaultVal.value);
  };

  const hasChanged = computed(
    () =>
      !isEqual(
        { ...params, filter: filterWithoutOperandIDs(params.filter), page: undefined },
        {
          ...toValue(defaultVal),
          filter: filterWithoutOperandIDs(toValue(defaultVal)?.filter),
          page: undefined,
        },
      ),
  );

  // Generally try to avoid mutating `syncEnabled` outside this component:
  // it's for emergencies only
  const syncEnabled = ref(true);
  async function disableSyncWhile(cb: () => Promise<unknown>) {
    syncEnabled.value = false;

    await cb();

    setTimeout(() => {
      syncEnabled.value = true;
    });
  }

  function updateParams() {
    if (!syncEnabled.value) return;

    disableSyncWhile(async () => {
      if (!hasChanged.value && params.page?.offset === 0) {
        if (route.query.q) await router.replace({ query: { q: undefined } });
        return;
      }

      const encoded = encodeSearchQueryParams(params);
      if (encoded.q !== route.query.q) {
        await router.replace({ query: encoded });
      }
    });
  }

  watch(params, updateParams, { deep: true });

  watch(
    mergedDefaultVal,
    (newVal) => {
      if (!newVal || !syncEnabled.value) return;
      // for each property in val, replace it in params
      for (const key of Object.keys(newVal)) {
        (params as any)[key] = (newVal as any)[key];
      }
    },
    { deep: true },
  );

  return { params, hasChanged, updateParams, reset, syncEnabled, disableSyncWhile };
}
